
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {Line2} from "../../../libs/three.js/lines/Line2.js";
import {LineGeometry} from "../../../libs/three.js/lines/LineGeometry.js";
import {LineMaterial} from "../../../libs/three.js/lines/LineMaterial.js";
import {Utils} from "../../utils.js";
import {DigitizationTools} from "../ToolRegistry.js";
import {FeatureVisual} from "../FeatureVisual.js";
import {createFeature} from "../Feature.js";
import {createStyledLabel} from "../DigitizationStyle.js";
import {computeScreenSpaceScale, ScreenSpaceSizes} from "../ScreenSpaceScale.js";
import {projectPolylineToScreen, midpointOf} from "../geometry/polylineGeometry.js";
import {computeConnectorScreen} from "../geometry/midlineConnector.js";

import {T, t} from "../i18n/index.js";
function createDashedLine(color){
	const geometry = new LineGeometry();
	geometry.setPositions([0, 0, 0, 0, 0, 0]);

	const material = new LineMaterial({
		color,
		linewidth: 2,
		dashSize: 3,
		gapSize: 2,
		dashed: true,
		resolution: new THREE.Vector2(1000, 1000),
	});
	material.depthTest = false;

	const line = new Line2(geometry, material);
	line.computeLineDistances();
	line.visible = false;

	return line;
}

function createMarker(color){
	// world-space radius 1, matching FeatureVisual's convention, so
	// ScreenSpaceScale's targetPixels values apply directly
	const geometry = new THREE.SphereGeometry(1, 16, 16); // kugle (var oktaeder/"pyramide")
	const material = new THREE.MeshLambertMaterial({color, depthTest: false, depthWrite: false});
	const mesh = new THREE.Mesh(geometry, material);
	mesh.visible = false;
	return mesh;
}

function setLinePositions(line, a, b){
	line.position.copy(a);
	line.geometry.setPositions([0, 0, 0, ...b.clone().sub(a).toArray()]);
	line.geometry.verticesNeedUpdate = true;
	line.geometry.computeBoundingSphere();
	line.computeLineDistances();
}

// "Calculated geometry" tool: digitizes two reference polylines (Vector 1,
// Vector 2), then lets the user walk a dynamic, perpendicular connector
// segment along both of them, locking a midpoint per click. Consecutive
// locked midpoints form the final result - a single LineString feature.
// Vector 1, Vector 2, and every connector are pure construction geometry and
// are discarded when the tool finishes; only the midpoint polyline persists.
//
// Conceptually this repeats the standalone Midpoint tool's own principle
// (two source points -> one calculated point) once per connector, then
// chains the results - see midpointTool.js's computeMidpoint for the same
// underlying average-of-two-points math (inlined here via midpointOf()
// since the two source points are dynamic, not user-clicked).
export const MidlineDigitizationTool = {
	id: "digitize_midline",
	name: T("Midterlinje"),
	category: "calculated",
	kind: "registration",
	toolbarGroup: "registration",
	toolbarOrder: 90,
	icon: "midline.svg",
	resultGeometryType: "LineString",
	featureType: T("Midterlinje"),

	tooltip: T("Opret en midtlinje mellem to digitaliserede vektorer ved hjælp af dynamiske forbindelsesvektorer."),

	// named states, per the multi-stage customCapture flow below - not
	// resolved through DigitizationModule._resolveInstruction() (that's only
	// for the generic single-visual capture flow), so runConnectorPhase()
	// picks the right key directly at each phase transition
	instructions: {
		vector1: T("Vektor 1: Venstreklik for at placere punkter langs den første vektor. Højreklik for at afslutte vektoren."),
		vector2: T("Vektor 2: Venstreklik for at placere punkter langs den anden vektor. Højreklik for at afslutte vektoren."),
		connectors: T("Flyt forbindelseslinjen til den ønskede position og venstreklik for at placere et midtpunkt. Gentag efter behov. Højreklik for at afslutte midtlinjen."),
	},

	customCapture(dm, tool){
		dm._showInstruction(tool.name, tool.instructions.vector1);

		dm._captureVector((v1) => {
			if(!v1) return; // too few points, or tool was switched away mid-capture

			dm._showInstruction(tool.name, tool.instructions.vector2);

			dm._captureVector((v2) => {
				if(!v2){
					dm.scene.remove(v1);
					return;
				}

				runConnectorPhase(dm, tool, v1, v2);
			});
		});
	},
	// Restore extension point (see DigitizationModule._buildLoadedFeature): the per-vertex role names the capture flow sets are part of
	// the finished midline's vertex labels; the construction vectors and connectors are never recreated.
	decorateLoaded(visual){
		visual.vertexRoleNames = visual.points.map((p, i) => t("Midtpunkt {0}", i + 1));
		for(let i = 0; i < visual.points.length; i++) visual._refreshVertexLabel(i);
	},
};

DigitizationTools.register(MidlineDigitizationTool);

function runConnectorPhase(dm, tool, v1, v2){
	const viewer = dm.viewer;
	const domElement = viewer.renderer.domElement;

	dm._showInstruction(tool.name, tool.instructions.connectors);

	const midline = new FeatureVisual("LineString");
	midline.vertexRoleNames = [];
	dm.scene.add(midline);

	const markerA = createMarker(0x00e5ff);
	const markerB = createMarker(0x00e5ff);
	const connectorLine = createDashedLine(0x00e5ff);
	const midpointMarker = createMarker(0xffdc00);
	const midpointLabel = createStyledLabel("");
	const previewEdge = createDashedLine(0xffdc00);

	dm.scene.add(markerA, markerB, connectorLine, midpointMarker, midpointLabel, previewEdge);

	let currentMidpoint = null;
	let finished = false;

	// persistent per-vector "walker" state (see polylineGeometry.js): keeps
	// the connector's endpoints gliding continuously along the local segment
	// neighbourhood instead of jumping to whichever segment is globally
	// nearest on a given frame, which is what avoids flicker/snapping at
	// intermediate vertices
	const walker1 = {};
	const walker2 = {};

	const updateScales = () => {
		const camera = viewer.scene.getActiveCamera();
		const size = viewer.renderer.getSize(new THREE.Vector2());
		const w = size.width;
		const h = size.height;
		const cameraPos = camera.position;

		for(const mesh of [markerA, markerB]){
			if(!mesh.visible) continue;
			const s = computeScreenSpaceScale(camera, mesh.getWorldPosition(new THREE.Vector3()), cameraPos, w, h, ScreenSpaceSizes.vertex);
			mesh.scale.set(s, s, s);
		}

		if(midpointMarker.visible){
			const s = computeScreenSpaceScale(camera, midpointMarker.getWorldPosition(new THREE.Vector3()), cameraPos, w, h, ScreenSpaceSizes.computedPoint);
			midpointMarker.scale.set(s, s, s);
		}

		if(midpointLabel.visible){
			const s = computeScreenSpaceScale(camera, midpointLabel.getWorldPosition(new THREE.Vector3()), cameraPos, w, h, ScreenSpaceSizes.label);
			midpointLabel.scale.set(s, s, s);
		}

		for(const line of [connectorLine, previewEdge]){
			line.material.resolution.set(w, h);
		}
	};

	const onUpdate = () => updateScales();
	viewer.addEventListener("update", onUpdate);

	const updatePreview = (mouse) => {
		const camera = viewer.scene.getActiveCamera();
		const size = viewer.renderer.getSize(new THREE.Vector2());
		const w = size.width;
		const h = size.height;

		const points1 = v1.points.map(p => p.position);
		const points2 = v2.points.map(p => p.position);
		const screen1 = projectPolylineToScreen(points1, camera, w, h);
		const screen2 = projectPolylineToScreen(points2, camera, w, h);
		const result = computeConnectorScreen(points1, points2, screen1, screen2, mouse, walker1, walker2);

		if(!result) return;

		markerA.position.copy(result.P1);
		markerB.position.copy(result.P2);
		markerA.visible = true;
		markerB.visible = true;

		setLinePositions(connectorLine, result.P1, result.P2);
		connectorLine.visible = true;

		const mid = midpointOf(result.P1, result.P2);
		currentMidpoint = mid;

		midpointMarker.position.copy(mid);
		midpointMarker.visible = true;

		const x = Utils.addCommas(mid.x.toFixed(3));
		const y = Utils.addCommas(mid.y.toFixed(3));
		const z = Utils.addCommas(mid.z.toFixed(3));
		midpointLabel.setText(t("Midtpunkt (preview)\nX: {0}\nY: {1}\nZ: {2}", x, y, z));
		midpointLabel.position.copy(mid);
		midpointLabel.visible = true;

		if(midline.points.length > 0){
			const last = midline.points[midline.points.length - 1].position;
			setLinePositions(previewEdge, last, mid);
			previewEdge.visible = true;
		}else{
			previewEdge.visible = false;
		}
	};

	const onMouseMove = (e) => {
		if(finished || dm.isPaused()) return;

		// primary driver is the raw 2D cursor position, not a point-cloud
		// raycast (the point cloud is sparse and gaps would otherwise freeze
		// the preview - see polylineGeometry.js's walkPolylineScreen)
		const mouse = dm._toCanvasMouse(e);
		updatePreview(mouse);
	};

	const removeConstructionGeometry = () => {
		dm.scene.remove(v1);
		dm.scene.remove(v2);
		dm.scene.remove(markerA);
		dm.scene.remove(markerB);
		dm.scene.remove(connectorLine);
		dm.scene.remove(midpointMarker);
		dm.scene.remove(midpointLabel);
		dm.scene.remove(previewEdge);
	};

	const cleanup = () => {
		domElement.removeEventListener('mousemove', onMouseMove, false);
		domElement.removeEventListener('mouseup', onMouseUp, false);
		viewer.removeEventListener('update', onUpdate);
		viewer.removeEventListener('cancel_insertions', onCancel);
	};

	const finalizeMidline = () => {
		if(midline.points.length < 2){
			dm.scene.remove(midline);
			return;
		}

		const coordinates = midline.toCoordinates();

		const feature = createFeature({
			geometryType: "LineString",
			coordinates,
			category: tool.category,
			tool: tool.id,
			featureType: tool.featureType || tool.name,
			name: `${tool.name} ${dm.featureManager.features.length + 1}`,
		});

		midline.vertexRoleNames = midline.points.map((p, i) => t("Midtpunkt {0}", i + 1));
		midline.setInfo(feature.properties.name, feature.properties.description);

		dm._wireFeatureLifecycle(feature, midline);

		dm.dispatchEvent({type: "feature_created", feature, visual: midline});
	};

	const finish = () => {
		if(finished) return;
		finished = true;
		cleanup();
		removeConstructionGeometry();
		finalizeMidline();

		// tool stays active: start a fresh Vector-1 -> Vector-2 -> connectors
		// cycle, same as every other digitization tool re-arming after use
		if(dm.activeTool === tool){
			dm.startInsertion(tool);
		}
	};

	const onCancel = (e) => {
		if(finished) return;
		finished = true;
		cleanup();
		removeConstructionGeometry();

		if(e && e.discard){
			// ESC: hard cancel - never commit a Midline feature from this
			// construction, even if midpoints were already locked
			dm.scene.remove(midline);
		}else{
			finalizeMidline();
		}
		// no restart here - a different tool took over, or the user
		// explicitly stopped this one
	};

	const onMouseUp = (e) => {
		if(finished || dm.isPaused() || dm._consumeCopyClickGuard()) return;

		if(e.button === THREE.MOUSE.LEFT){
			if(currentMidpoint){
				const index = midline.points.length;
				midline.appendAtEnd(currentMidpoint.clone());
				midline.vertexRoleNames[index] = t("Midtpunkt {0}", index + 1);
				midline._refreshVertexLabel(index);

				// the just-committed point is now a real, permanent vertex -
				// hide the dynamic preview immediately (same operation, not
				// on the next mousemove/redraw) so it can never coincide
				// on-screen with it, and clear currentMidpoint so a second,
				// stray mouseup before any mousemove can't re-commit the
				// same stale position as a duplicate vertex
				currentMidpoint = null;
				markerA.visible = false;
				markerB.visible = false;
				connectorLine.visible = false;
				midpointMarker.visible = false;
				midpointLabel.visible = false;
				previewEdge.visible = false;
			}
		}else if(e.button === THREE.MOUSE.RIGHT){
			finish();
		}
	};

	domElement.addEventListener('mousemove', onMouseMove, false);
	domElement.addEventListener('mouseup', onMouseUp, false);
	viewer.addEventListener('cancel_insertions', onCancel);
}
